import React, { useState, useEffect } from "react";
import { Button, Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";
import axios from "axios";
import { API_URL } from "../../utils/auth";
import Auth from "../../utils/auth";
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import "./EmailManagementModal.css";

const EmailManagementModal = ({ 
  open, 
  onClose, 
  clientId, 
  workspaceId, 
  additionalEmails = [], 
  setAdditionalEmails
}) => {
  const [newEmail, setNewEmail] = useState("");
  const [isAddingEmail, setIsAddingEmail] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [emailToDelete, setEmailToDelete] = useState(null);
  const [showNewEmailInput, setShowNewEmailInput] = useState(false);
  const [primaryEmail, setPrimaryEmail] = useState("");

  // Ensure additionalEmails is always an array
  const emailsList = Array.isArray(additionalEmails) ? additionalEmails : [];

  // Fetch emails when modal opens
  useEffect(() => {
    const fetchEmails = async () => {
      if (!open || !clientId || !workspaceId) return;

      try {
        const response = await axios.get(
          `${API_URL}/clients/client-email?workspaceId=${workspaceId}&clientId=${clientId}`
        );
        
        if (response.data.success) {
          setPrimaryEmail(response.data.primaryEmail || "");
          setAdditionalEmails(response.data.additionalEmails || []);
        }
      } catch (error) {
        console.error("Error fetching emails:", error);
        toast.error("Error loading emails");
      }
    };

    fetchEmails();
  }, [open, clientId, workspaceId]);

  const handleAddEmail = async () => {
    if (!newEmail || !newEmail.trim()) {
      toast.warning("Please enter a valid email.");
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newEmail)) {
      toast.warning("Please enter a valid email format.");
      return;
    }

    setIsAddingEmail(true);
    try {
      const response = await axios.post(
        `${API_URL}/clients/client-email`,
        {
          workspaceId,
          clientId,
          email: newEmail.trim()
        },
        {
          headers: { 
            "Content-Type": "application/json"
          }
        }
      );
      
      // Extract the email object from response
      const newEmailObj = response.data.email || response.data;
      setAdditionalEmails([...emailsList, newEmailObj]);
      setNewEmail("");
      setShowNewEmailInput(false);
      toast.success("Email added successfully!");
    } catch (error) {
      console.error("Error adding email:", error);
      toast.error(error.response?.data?.error || error.response?.data?.message || "Error adding email");
    } finally {
      setIsAddingEmail(false);
    }
  };

  const handleDeleteClick = (emailObj) => {
    setEmailToDelete(emailObj);
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = async () => {
    if (!emailToDelete) return;

    console.log('Delete params:', {
      workspaceId,
      clientId,
      emailId: emailToDelete.id
    });

    try {
      const response = await axios({
        method: 'delete',
        url: `${API_URL}/clients/client-email`,
        headers: { 
          "Content-Type": "application/json"
        },
        data: JSON.stringify({
          workspaceId: Number(workspaceId),
          clientId: Number(clientId),
          emailId: Number(emailToDelete.id)
        })
      });
      
      setAdditionalEmails(emailsList.filter(email => email.id !== emailToDelete.id));
      toast.success("Email deleted successfully!");
    } catch (error) {
      console.error("Error deleting email:", error);
      console.error("Error response:", error.response?.data);
      toast.error(error.response?.data?.error || error.response?.data?.message || "Error deleting email");
    } finally {
      setShowDeleteConfirm(false);
      setEmailToDelete(null);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteConfirm(false);
    setEmailToDelete(null);
  };

  const handleClose = () => {
    setNewEmail("");
    setShowNewEmailInput(false);
    onClose();
  };

  return (
    <>
      <Dialog 
        open={open} 
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          style: {
            borderRadius: '12px',
            padding: '10px'
          }
        }}
      >
        <DialogTitle style={{ 
          fontFamily: "'Albert Sans', sans-serif",
          fontWeight: 600,
          color: '#291B3B',
          fontSize: '24px'
        }}>
          Manage Additional Emails
        </DialogTitle>
        
        <DialogContent>
          <div className="email-modal-content">
            {/* Email List */}
            <div className="email-list-container">
              {/* Primary Email */}
              {primaryEmail && (
                <div className="email-list-item primary-email-item">
                  <span className="email-list-text">{primaryEmail}</span>
                  <span className="primary-email-badge">Primary</span>
                </div>
              )}

              {/* Additional Emails */}
              {emailsList.length === 0 && !primaryEmail ? (
                <p className="no-emails-text">No emails yet</p>
              ) : (
                emailsList.map((emailObj) => (
                  <div key={emailObj.id} className="email-list-item">
                    <span className="email-list-text">{emailObj.email}</span>
                    <button
                      type="button"
                      className="email-delete-btn"
                      onClick={() => handleDeleteClick(emailObj)}
                      title="Delete email"
                    >
                      ×
                    </button>
                  </div>
                ))
              )}
            </div>

            {/* Add New Email Section */}
            {!showNewEmailInput ? (
              <button
                type="button"
                className="add-new-email-btn"
                onClick={() => setShowNewEmailInput(true)}
              >
                + Add New Email
              </button>
            ) : (
              <div className="new-email-input-section">
                <input
                  type="email"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  placeholder="Enter new email"
                  className="new-email-input"
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      handleAddEmail();
                    }
                  }}
                />
                <div className="new-email-actions">
                  <Button
                    variant="contained"
                    onClick={handleAddEmail}
                    disabled={isAddingEmail || !newEmail}
                    style={{
                      backgroundColor: 'rgba(87, 42, 241, 1)',
                      color: 'white',
                      borderRadius: '8px',
                      textTransform: 'none',
                      fontFamily: "'Red Hat Text', sans-serif",
                      padding: '8px 20px'
                    }}
                  >
                    {isAddingEmail ? "Saving..." : "Save"}
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() => {
                      setNewEmail("");
                      setShowNewEmailInput(false);
                    }}
                    style={{
                      borderColor: 'rgba(87, 42, 241, 1)',
                      color: 'rgba(87, 42, 241, 1)',
                      borderRadius: '8px',
                      textTransform: 'none',
                      fontFamily: "'Red Hat Text', sans-serif",
                      padding: '8px 20px'
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
        
        <DialogActions style={{ padding: '10px 24px 20px' }}>
          <Button
            onClick={handleClose}
            style={{
              backgroundColor: 'rgba(87, 42, 241, 1)',
              color: 'white',
              borderRadius: '8px',
              textTransform: 'none',
              fontFamily: "'Red Hat Text', sans-serif",
              padding: '8px 24px'
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={showDeleteConfirm}
        onClose={handleCancelDelete}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          style: {
            borderRadius: '12px',
            padding: '10px'
          }
        }}
      >
        <DialogTitle style={{ 
          fontFamily: "'Albert Sans', sans-serif",
          fontWeight: 600,
          color: '#291B3B',
          fontSize: '20px'
        }}>
          Confirm Delete
        </DialogTitle>
        <DialogContent>
          <p style={{ 
            fontFamily: "'Red Hat Text', sans-serif",
            color: '#291B3B',
            fontSize: '16px'
          }}>
            Are you sure you want to delete this email?
            <br />
            <strong>{emailToDelete?.email}</strong>
          </p>
        </DialogContent>
        <DialogActions style={{ padding: '10px 24px 20px' }}>
          <Button
            onClick={handleCancelDelete}
            style={{
              borderColor: 'rgba(87, 42, 241, 1)',
              color: 'rgba(87, 42, 241, 1)',
              borderRadius: '8px',
              textTransform: 'none',
              fontFamily: "'Red Hat Text', sans-serif",
              padding: '8px 20px'
            }}
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            style={{
              backgroundColor: 'rgba(255, 82, 82, 1)',
              color: 'white',
              borderRadius: '8px',
              textTransform: 'none',
              fontFamily: "'Red Hat Text', sans-serif",
              padding: '8px 20px'
            }}
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default EmailManagementModal;
